import React from 'react';
import { Typography } from '@mui/material';
import LegalPageLayout from './LegalPageLayout';

const AcceptableUsePolicy = () => {
  return (
    <LegalPageLayout
      title="Acceptable Use Policy"
      updated="October 13, 2026"
    >
      <Typography paragraph>
        This Acceptable Use Policy governs conduct and content
        on HaggleFreight, a transportation marketplace operated
        by GoCourier Inc. It supplements the Terms of Service
        when presented and accepted.
      </Typography>

      <Typography paragraph>
        Users must accept the applicable terms and policies
        before posting listings, submitting bids, uploading
        content, or using marketplace communications.
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Prohibited Activity
      </Typography>

      <Typography paragraph>
        Do not use the Platform for fraud, unlawful conduct,
        impersonation, false documentation, deception, trafficking,
        exploitation, or infringement of another person's rights.
      </Typography>

      <Typography paragraph>
        Honest price negotiation and lawful competition are
        permitted. Fake bids, fabricated demand, deceptive
        reviews, unlawful collusion, and dishonest manipulation
        of transactions are prohibited.
      </Typography>

      <Typography paragraph>
        Do not misrepresent whose business, operating authority,
        insurance, vehicle, or personnel will perform a shipment.
        Do not conceal information material to safe and lawful
        performance.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Prohibited Shipments
      </Typography>

      <Typography paragraph>
        Illegal goods, stolen property, counterfeit goods, and
        shipments prohibited by applicable law are always
        prohibited. Do not conceal cargo contents, evade
        inspections, or misrepresent a shipment's regulatory status.
      </Typography>

      <Typography paragraph>
        Unless a published HaggleFreight policy expressly supports
        the category and its requirements are satisfied before
        posting, the Platform does not permit weapons or
        ammunition, explosives, hazardous materials, regulated
        chemicals, controlled substances, live animals, human
        remains, cash, or negotiable instruments.
      </Typography>

      <Typography paragraph>
        A user's willingness to accept cargo, an informal support
        message, or the ability to enter an item in a listing
        does not override a legal prohibition or the Platform's
        published restrictions.
      </Typography>

      <Typography variant="h5" gutterBottom>
        3. Restricted or Special-Handling Shipments
      </Typography>

      <Typography paragraph>
        Disclose lawful special requirements before acceptance,
        including temperature control, unusual dimensions,
        loading equipment, permits, packaging, documentation,
        and facility-access conditions.
      </Typography>

      <Typography paragraph>
        Only qualified Transportation Providers may undertake
        work requiring particular authority, equipment, or
        professional handling. Disclosure alone does not make
        an unsupported category permissible.
      </Typography>

      <Typography variant="h5" gutterBottom>
        4. Safety
      </Typography>

      <Typography paragraph>
        Do not create unsafe pickup, loading, transportation,
        or delivery conditions or instruct another person to
        violate safety requirements.
      </Typography>

      <Typography paragraph>
        Transportation Providers may refuse unsafe or unlawful
        work, subject to lawful duties concerning cargo already
        in their custody. Commercial pressure, estimated arrival
        times, and customer requests do not override those duties.
      </Typography>

      <Typography paragraph>
        Do not use the Platform in a manner that distracts from
        safe vehicle operation. For immediate danger, contact
        appropriate emergency services rather than relying on
        Platform support.
      </Typography>

      <Typography variant="h5" gutterBottom>
        5. Platform and Security Abuse
      </Typography>

      <Typography paragraph>
        Do not access accounts or information without authorization,
        bypass access controls, introduce malicious code,
        attack infrastructure, overload APIs, or interfere with
        service availability.
      </Typography>

      <Typography paragraph>
        Do not scrape private information, use unauthorized
        automated access, or reverse engineer the Platform
        except to the extent applicable law permits.
      </Typography>

      <Typography paragraph>
        Report suspected security problems responsibly.
        Do not access additional information, disrupt operations,
        or demand payment through threats. This policy does not
        grant permission for security testing beyond your lawful
        authorization.
      </Typography>

      <Typography variant="h5" gutterBottom>
        6. Payment Abuse
      </Typography>

      <Typography paragraph>
        Do not use stolen payment methods, fabricate refund
        claims, falsify chargeback evidence, redirect another
        user's payout without authorization, or exploit
        promotions through deception.
      </Typography>

      <Typography paragraph>
        Good-faith payment disputes, refund requests, and
        complaints are not themselves abuse. Do not seek
        duplicate recovery for the same loss.
      </Typography>

      <Typography paragraph>
        Do not evade a specific charge or obligation that was
        properly disclosed and accepted. No commission,
        subscription fee, or penalty is created merely by
        this policy.
      </Typography>

      <Typography variant="h5" gutterBottom>
        7. Communication Abuse and Objectionable Content
      </Typography>

      <Typography paragraph>
        Do not post or send threats, harassment, discriminatory
        abuse, hate speech, sexually explicit material, child
        sexual abuse or exploitation material, scams, or
        content promoting unlawful violence or trafficking.
      </Typography>

      <Typography paragraph>
        Do not publish private information to intimidate someone,
        threaten retaliation for a legitimate complaint,
        impersonate support personnel, or solicit account
        credentials.
      </Typography>

      <Typography paragraph>
        Communications must not be used for spam, unsolicited
        mass marketing, deceptive payment requests, or unrelated
        abusive contact. Honest criticism and lawful complaints
        are not prohibited merely because they are unfavorable.
      </Typography>

      <Typography variant="h5" gutterBottom>
        8. Direct Contact and Off-Platform Conduct
      </Typography>

      <Typography paragraph>
        Ordinary lawful direct communications and independent
        business relationships are not, by themselves,
        prohibited.
      </Typography>

      <Typography paragraph>
        Do not use off-platform contact to harass users, solicit
        credentials, commit fraud, conceal prohibited cargo,
        bypass an account restriction, or evade an existing
        accepted obligation.
      </Typography>

      <Typography paragraph>
        This policy does not impose exclusivity or create a
        charge merely for communicating outside the Platform.
        Features and records associated with a supported
        Platform transaction may not be available for a separate
        transaction conducted outside it.
      </Typography>

      <Typography variant="h5" gutterBottom>
        9. False Information and Content Rights
      </Typography>

      <Typography paragraph>
        Provide truthful identity, business, vehicle, authority,
        insurance, shipment, payment, and status information.
        Do not fabricate photographs, signatures, timestamps,
        delivery evidence, reviews, or claim documents.
      </Typography>

      <Typography paragraph>
        Submit only content you own or are authorized to use
        and share. Respect privacy, confidentiality,
        intellectual-property rights, and the intended audience
        of messages and documents.
      </Typography>

      <Typography paragraph>
        Do not sell other users' information or reuse it for
        unsolicited marketing. The User Content provisions in
        the Terms explain the permissions granted to
        GoCourier Inc. to operate the Platform.
      </Typography>

      <Typography variant="h5" gutterBottom>
        10. Reporting, Blocking, and Enforcement
      </Typography>

      <Typography paragraph>
        Report objectionable content or users through the
        in-app reporting controls or support@hagglefreight.com.
        Identify the relevant account, listing, or message
        and explain the issue without unnecessarily disclosing
        sensitive information.
      </Typography>

      <Typography paragraph>
        Use available blocking controls to prevent unwanted
        interactions. Do not evade another user's block through
        a replacement account or another communication channel.
      </Typography>

      <Typography paragraph>
        We review reports and may remove or restrict content,
        limit communications, suspend access, or terminate
        accounts for serious or repeated violations. Urgent
        action may be necessary for safety, security, or legal
        compliance. Where appropriate and lawful, we will explain
        an action and provide a way to request review.
      </Typography>

      <Typography paragraph>
        An account violation does not automatically forfeit
        payments or payouts. Financial restrictions require
        a separate lawful basis and must comply with applicable
        accepted terms and provider requirements.
      </Typography>

      <Typography paragraph>
        Moderation does not guarantee that every submission has
        been screened or that all harmful conduct will be
        prevented. Do not submit knowingly false reports or
        retaliate against a good-faith reporter.
      </Typography>

      <Typography variant="h5" gutterBottom>
        11. Contact
      </Typography>

      <Typography paragraph>
        Send reports or policy questions to
        support@hagglefreight.com. For an intellectual-property
        complaint, identify the protected work or right, the
        material at issue, your contact information, and your
        authority to raise the concern.
      </Typography>

      <Typography paragraph>
        Material changes are handled under the Terms of Service.
        This policy does not override mandatory rights,
        authorized legal processes, or the right to communicate
        with a regulator.
      </Typography>
    </LegalPageLayout>
  );
};

export default AcceptableUsePolicy;